/**
 * S12b CADCORE-GRIPS-SELECT-WIRE — typed HOT-GRIP selection model.
 *
 * AutoCAD's grip interaction has three grip STATES: cold (GRIPCOLOR — a
 * selected object's idle grips), hover (GRIPHOVER — cursor over a grip), and
 * HOT (GRIPHOT — a grip the user has picked / warmed, the one a stretch |
 * move | rotate | scale | mirror edit acts on). Before S12b the overlay's
 * hot state was hard-wired `false` (`gripsOverlay.tsx` — "always false … all
 * displayed grips render cold"), so GRIPHOT was an accepted-but-never-used
 * sysvar (a placeholder, CLAUDE.md ПРИОРИТЕТ #1).
 *
 * This module is the typed selection model that state lives in — a STRUCTURED
 * map of {@link HotGripRef}s (each naming its entity, grip slot, kind, WCS
 * position and the typed `patch` = which `DrawingEntity` field a drag on it
 * rewrites), NOT a boolean flag and NOT a single "the hot grip" slot. AutoCAD
 * warms ONE grip on a plain click and ADDS more on Shift-click (a multi-grip
 * stretch set), so the model is inherently a multi-instance set — exactly the
 * "полная multi-instance иерархия грипов … не один flat grip" the slice asks
 * for. It mirrors the established scene3d external-store pattern
 * (`cameraSnapshot.ts`) so the overlay subscribes via `useSyncExternalStore`.
 *
 * Scope (задел под L8): S12b wires CLICK → hot-grip (the grabbed grip warms,
 * visibly recolouring to GRIPHOT) and prunes the set to the live selection.
 * The hot-grip-driven edit MODE cycle (Ctrl: stretch/move/rotate/scale/mirror)
 * and the typed grip commit are L8 (`propertyReducer.ts`, its sole owner),
 * which consumes this model's `patch` — this module does not import or touch
 * the reducer.
 *
 * GRIPS-INTERACTION PR-1 (§8.2 / §5.11): this module additionally tracks the
 * single ARMED grip — the grip a CLICK (not a drag) has put into AutoCAD's
 * modal "** STRETCH **" phase, awaiting its second (commit) click. Armed is a
 * state DISTINCT from hot/hover: a hot grip is merely warmed (selected for the
 * edit set, e.g. a Shift-click multi-grip stretch), whereas the armed grip is
 * the one live-tracking the cursor with an armed indicator. At most ONE grip is
 * armed at a time (a click-arm stretch acts on a single grip); the multi-grip
 * warmed SET stays in the hot map above.
 *
 * GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2): this module
 * additionally tracks `armedEditMode` — the modal grip prompt's CURRENT edit
 * mode (Stretch / Move / Rotate / Scale / Mirror). Pressing Enter or Spacebar
 * while a grip is armed cycles it (`cycleArmedEditMode`), source-verified
 * against Autodesk's docs (see `gripInteractionConstants.ts`'s
 * `GRIP_MODE_LABELS` doc comment for the citation + the Ctrl-is-NOT-this
 * correction). A fresh arm always restarts at Stretch (AutoCAD parity — the
 * mode cycle does not persist across grip picks).
 */

import type { GripPatchTarget } from "@dxf-ai-editor/cad-core";

import type { GripKind } from "./computeGrips";
import {
  nextGripEditMode,
  type GripEditMode,
} from "./gripInteractionConstants";

/** A single warmed (hot) grip in the typed selection model. */
export interface HotGripRef {
  readonly entityId: string;
  /** Stable per-entity grip slot id ("start" / "vertex-2" / "quad-right" …). */
  readonly gripId: string;
  readonly kind: GripKind;
  /** WCS position the grip was warmed at. */
  readonly world: { readonly x: number; readonly y: number };
  /**
   * Typed `DrawingEntity` field a drag on this grip rewrites (from the
   * cad-core grip that produced it). Absent for legacy string-bag grips and
   * for compound grips with no single field (e.g. a Line midpoint). L8's
   * typed commit dispatches on this.
   */
  readonly patch?: GripPatchTarget;
}

/** Canonical `entityId:gripId` key for the hot-grip map / overlay lookup. */
export function hotGripKey(entityId: string, gripId: string): string {
  return `${entityId}:${gripId}`;
}

let hotGrips: ReadonlyMap<string, HotGripRef> = new Map();
/**
 * GRIPS-INTERACTION PR-1 — the single CLICK-ARMED grip (AutoCAD modal
 * "** STRETCH **" phase), or `null` when no click-arm is in progress. Shares
 * the same notification channel (`emit` / `listeners`) as the hot map so the
 * overlay re-renders on either change from one `subscribeHotGrips`.
 */
let armedGrip: HotGripRef | null = null;
/**
 * GRIPS-INTERACTION PR-2 — the armed grip's CURRENT edit mode. Meaningless
 * while `armedGrip` is `null` (no modal prompt is open); reset to "stretch"
 * whenever a NEW grip arms or the armed grip clears, so the cycle always
 * restarts fresh (AutoCAD parity — the mode does not carry over between
 * separate grip picks).
 */
let armedEditMode: GripEditMode = "stretch";
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to hot-grip changes (`useSyncExternalStore` contract). */
export function subscribeHotGrips(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Current hot-grip map snapshot. Stable identity between mutations (the same
 * `Map` reference is returned until a setter replaces it), so
 * `useSyncExternalStore` never loops.
 */
export function getHotGripsSnapshot(): ReadonlyMap<string, HotGripRef> {
  return hotGrips;
}

/**
 * Warm a grip. `additive` (Shift-click parity) KEEPS the existing hot set and
 * adds this grip; otherwise it REPLACES the set with just this grip (plain
 * click). Re-warming an already-hot grip is idempotent (same key overwrites).
 */
export function setHotGrip(ref: HotGripRef, additive = false): void {
  const next = new Map(additive ? hotGrips : []);
  next.set(hotGripKey(ref.entityId, ref.gripId), ref);
  hotGrips = next;
  emit();
}

/** Clear all hot grips + any armed grip (e.g. a click on empty space / deselect-all). */
export function clearHotGrips(): void {
  if (hotGrips.size === 0 && armedGrip === null) return;
  hotGrips = new Map();
  armedGrip = null;
  // GRIPS-INTERACTION PR-2 — mirrors `clearArmedGrip`'s mode reset so a stale
  // cycled mode never survives a deselect-all into the NEXT arm.
  armedEditMode = "stretch";
  emit();
}

/** True when the given grip slot is currently hot. */
export function isHotGrip(entityId: string, gripId: string): boolean {
  return hotGrips.has(hotGripKey(entityId, gripId));
}

/**
 * GRIPS-INTERACTION PR-1 — current armed grip snapshot (or `null`). Stable
 * identity between mutations (the SAME ref is returned until a setter replaces
 * it), so `useSyncExternalStore` never loops.
 */
export function getArmedGripSnapshot(): HotGripRef | null {
  return armedGrip;
}

/**
 * ARM a grip (AutoCAD click-arm → modal "** STRETCH **"). Replaces any prior
 * armed grip (only one is armed at a time). Idempotent per identical ref.
 * GRIPS-INTERACTION PR-2 — a fresh arm always RESTARTS the edit-mode cycle at
 * Stretch, even when re-arming the SAME grip mid-session (AutoCAD parity: the
 * modal prompt's mode never carries over from a previous, already-ended pick).
 */
export function setArmedGrip(ref: HotGripRef): void {
  armedGrip = ref;
  armedEditMode = "stretch";
  emit();
}

/** Clear the armed grip (commit / Escape / abort). No-op + no notify when idle. */
export function clearArmedGrip(): void {
  if (armedGrip === null) return;
  armedGrip = null;
  // GRIPS-INTERACTION PR-2 — clear the mode alongside the grip so the NEXT arm
  // (of this or any other grip) starts at Stretch, not wherever the cycle was
  // left.
  armedEditMode = "stretch";
  emit();
}

/**
 * GRIPS-INTERACTION PR-2 — current armed edit-mode snapshot. Stable primitive
 * return (fine for `useSyncExternalStore`'s `Object.is` identity check).
 * Meaningless while no grip is armed (`getArmedGripSnapshot() === null`) —
 * callers that care gate on the armed grip first, same as the mode label
 * itself is meaningless outside the modal prompt.
 */
export function getArmedEditModeSnapshot(): GripEditMode {
  return armedEditMode;
}

/**
 * GRIPS-INTERACTION PR-2 — cycle the armed grip's edit mode (Enter/Space
 * while armed; AutoCAD-verified, NOT Ctrl — see `gripInteractionConstants.ts`).
 * No-op (and no `emit`) when idle (no grip armed), mirroring
 * `clearArmedGrip`'s no-op-when-idle guard — cycling a mode that has no modal
 * prompt open would be meaningless and would spuriously re-render every
 * `subscribeHotGrips` listener.
 */
export function cycleArmedEditMode(): void {
  if (armedGrip === null) return;
  armedEditMode = nextGripEditMode(armedEditMode);
  emit();
}

/** True when the given grip slot is the currently armed grip. */
export function isArmedGrip(entityId: string, gripId: string): boolean {
  return (
    armedGrip !== null &&
    armedGrip.entityId === entityId &&
    armedGrip.gripId === gripId
  );
}

/**
 * Prune the hot set to grips whose entity is still in `entityIds` (the live
 * selection). AutoCAD drops a grip's hot state the moment its object leaves
 * the selection set; a no-op (same reference) when nothing is pruned so
 * subscribers don't re-render needlessly.
 */
export function retainHotGripsForEntities(
  entityIds: ReadonlySet<string>,
): void {
  let changed = false;
  if (hotGrips.size > 0) {
    const next = new Map<string, HotGripRef>();
    for (const [key, ref] of hotGrips) {
      if (entityIds.has(ref.entityId)) next.set(key, ref);
      else changed = true;
    }
    if (changed) hotGrips = next;
  }
  // The armed grip drops the moment its object leaves the selection too (same
  // AutoCAD rule as the hot set). Guarded so a no-op leaves BOTH refs stable
  // (no spurious `useSyncExternalStore` re-render).
  if (armedGrip !== null && !entityIds.has(armedGrip.entityId)) {
    armedGrip = null;
    // GRIPS-INTERACTION PR-2 — mirrors `clearArmedGrip`'s mode reset.
    armedEditMode = "stretch";
    changed = true;
  }
  if (changed) emit();
}

/**
 * Reset the store to empty WITHOUT notifying subscribers — test-only teardown
 * so one test's warmed grips never leak into the next (the module singleton
 * persists across a file's tests).
 */
export function __resetHotGripsForTest(): void {
  hotGrips = new Map();
  armedGrip = null;
  armedEditMode = "stretch";
}
